// Turning a planet's carousel, from whichever device asked for it.
//
// Two inputs drive the same ring and they report movement in different units: a drag gives
// pixels of pointer travel (MainScene reads them off `clientX` against its last anchor), and a
// wheel gives notches, which are pixels on some devices and whole *lines* or *pages* on others
// (`deltaMode`). Both are reduced here to the one thing `rotateRing` takes — a signed number of
// radians — so the two gestures cannot drift into two speeds, and so the arithmetic is testable
// without a scene. GlobalFunctions and this module are the pair the other way round: that one
// owns where a card goes, this one owns how far the ring turned to put it there.

/**
 * Radians of ring rotation per pixel of movement.
 *
 * Shared with the drag path rather than duplicated in it. One wheel notch is about 100 of these
 * on the common devices — a little over a radian, which is roughly one card's share of a
 * six-card ring — so a notch and a 100px drag are the same gesture, and neither is quantised:
 * the ring glides to any angle between two cards, which is what makes the rotation continuous.
 */
export const RADIANS_PER_PIXEL = 0.01;

/** What a line and a page are worth, for the devices that count in those instead of pixels. */
export const LINE_PIXELS = 16;
export const PAGE_PIXELS = 800;

function toPixels(delta, deltaMode) {
    if (deltaMode === 1) return delta * LINE_PIXELS;
    if (deltaMode === 2) return delta * PAGE_PIXELS;
    return delta;
}

/**
 * How far a wheel event moved, in pixels, on whichever axis it moved further in.
 *
 * A mouse wheel only ever reports a vertical notch; a trackpad swipes in both directions and
 * reports both, and a two-finger swipe *across* is the one that reads as turning a carousel.
 * Taking the larger of the two is what lets both devices drive the ring without the handler
 * needing to know which one it is talking to.
 *
 * A missing field is read as no movement rather than as arithmetic on `undefined`, so an event
 * from a source that does not fill the whole record turns nothing instead of returning NaN —
 * which would reach `userData.angle` and strand the ring there for good.
 */
export function wheelPixels(event) {
    const x = toPixels(event.deltaX || 0, event.deltaMode);
    const y = toPixels(event.deltaY || 0, event.deltaMode);

    return Math.abs(y) >= Math.abs(x) ? y : x;
}

/**
 * The ring rotation one wheel event asks for, in radians, in the direction a drag would turn it.
 *
 * A trackpad pinch arrives as a `wheel` event with `ctrlKey` set — the page is being zoomed, and
 * the ring is not what is being turned — so it is answered with no rotation at all. The caller
 * still sees the event; it is up to the caller not to take the gesture away from the browser.
 */
export function wheelRotation(event) {
    if (event.ctrlKey) return 0;

    return wheelPixels(event) * RADIANS_PER_PIXEL;
}
